import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useApi } from "@/lib/api";

function timeAgo(iso) {
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const api = useApi();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setData(res.data);
    } catch {}
  }, [api]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    load();
    if ((n.kind === "landing_overdue" || n.kind === "tracking_synced") && n.tracking_id) navigate(`/tracking/${n.tracking_id}`);
    else if (n.kind === "quote_inbox" && !n.trip_id) navigate("/quote-inbox");
    else if (n.trip_id) navigate(`/trips/${n.trip_id}`);
    else if (n.kind?.startsWith("jet_card")) navigate("/jet-cards");
  };

  const readAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative p-2 text-zinc-300 hover:text-[#5B7FD1] transition-colors" data-testid="notification-bell-btn" aria-label="Notifications">
          <Bell size={18} strokeWidth={1.5} />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] px-1 grid place-items-center text-[10px] font-semibold bg-[#1E3A8A] text-white rounded-full" data-testid="notification-unread-count">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0 bg-[#111319] border-white/10" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.07]">
          <span className="eyebrow">Notifications</span>
          <button onClick={readAll} className="text-[11px] text-zinc-400 hover:text-white" data-testid="notification-read-all-btn">Mark all read</button>
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {data.items.length === 0 && <p className="px-4 py-8 text-sm text-zinc-500 text-center" data-testid="notification-empty">No notifications yet</p>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-white/[0.05] hover:bg-white/[0.03] transition-colors flex gap-3 ${n.read ? "opacity-60" : ""}`}>
              <span className={`mt-1.5 h-1.5 w-1.5 rounded-full shrink-0 ${n.read ? "bg-transparent" : "bg-[#5B7FD1]"}`} />
              <span className="flex-1">
                <span className="block text-[13px] text-zinc-200 leading-snug">{n.message}</span>
                <span className="block text-[11px] text-zinc-500 mt-1 font-mono-data">{timeAgo(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
